import { useState } from "react";
import { X } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MemberRole } from "@kandoo/shared";
import { api } from "../api/client";
import { Modal } from "./Modal";
import { ConfirmButton } from "./ConfirmButton";

interface ShareBoardProps {
  boardId: string;
  isOwner: boolean;
  currentOrgId: string | null;
  onClose: () => void;
}

/** Board sharing dialog: members (invite/role/remove) and org assignment. */
export function ShareBoard({
  boardId,
  isOwner,
  currentOrgId,
  onClose,
}: ShareBoardProps) {
  const qc = useQueryClient();
  const membersQ = useQuery({
    queryKey: ["board-members", boardId],
    queryFn: () => api.members.list(boardId),
  });
  const orgsQ = useQuery({ queryKey: ["orgs"], queryFn: () => api.orgs.list() });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["board-members", boardId] });
  const invalidateBoard = () =>
    qc.invalidateQueries({ queryKey: ["board", boardId] });

  const invite = useMutation({
    mutationFn: (b: { username: string; role: MemberRole }) =>
      api.members.invite(boardId, b),
    onSuccess: invalidate,
  });
  const setRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: MemberRole }) =>
      api.members.setRole(boardId, userId, role),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api.members.remove(boardId, userId),
    onSuccess: invalidate,
  });
  const assignOrg = useMutation({
    mutationFn: (orgId: string | null) =>
      api.members.assignOrg(boardId, { orgId }),
    onSuccess: () => {
      invalidate();
      invalidateBoard();
    },
  });

  const [username, setUsername] = useState("");
  const [role, setRoleValue] = useState<MemberRole>("editor");

  function submitInvite(e: React.FormEvent) {
    e.preventDefault();
    const u = username.trim();
    if (!u) return;
    invite.mutate(
      { username: u, role },
      { onSuccess: () => setUsername("") },
    );
  }

  const members = membersQ.data ?? [];

  return (
    <Modal onClose={onClose} ariaLabel="Share board">
      <div className="p-5 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Share board</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={16} />
          </button>
        </div>

        {isOwner && (
          <form onSubmit={submitInvite} className="mb-4 flex flex-wrap gap-2">
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Invite by username"
              className="min-w-40 flex-1 rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-400"
            />
            <select
              value={role}
              onChange={(e) => setRoleValue(e.target.value as MemberRole)}
              className="rounded border border-slate-300 px-2 py-2 text-sm"
            >
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </select>
            <button
              type="submit"
              disabled={invite.isPending}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            >
              Add
            </button>
          </form>
        )}
        {invite.isError && (
          <p className="mb-3 text-sm text-red-600">
            {(invite.error as Error).message}
          </p>
        )}

        <ul className="divide-y divide-slate-100">
          {members.map((m) => (
            <li key={m.userId} className="flex items-center gap-3 py-2">
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-800">
                  {m.displayName || m.username}
                </div>
                <div className="text-xs text-slate-500">@{m.username}</div>
              </div>
              {m.isOwner ? (
                <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                  Owner
                </span>
              ) : m.source === "org" || m.source === "service" ? (
                // Inherited access — from the organization, or (for a service
                // user) from its owner. Neither is a board_members row, so
                // there's nothing here to re-role or remove; that's managed on
                // the org, or by deleting the service user.
                <span
                  title={
                    m.source === "org"
                      ? "Access comes from the organization — manage it there"
                      : "Service user — inherits its owner's access"
                  }
                  className="rounded bg-slate-100 px-2 py-0.5 text-xs capitalize text-slate-500"
                >
                  {m.role} · {m.source === "org" ? "via org" : "service user"}
                </span>
              ) : isOwner ? (
                <>
                  <select
                    value={m.role}
                    onChange={(e) =>
                      setRole.mutate({
                        userId: m.userId,
                        role: e.target.value as MemberRole,
                      })
                    }
                    className="rounded border border-slate-300 px-2 py-1 text-xs"
                  >
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <ConfirmButton
                    onConfirm={() => remove.mutate(m.userId)}
                    title="Remove member"
                    className="rounded px-1.5 py-1 text-xs text-slate-400 hover:bg-red-50 hover:text-red-600"
                    confirmClassName="rounded bg-red-600 px-1.5 text-xs text-white"
                    confirmLabel="Remove?"
                  >
                    <X size={14} />
                  </ConfirmButton>
                </>
              ) : (
                <span className="text-xs capitalize text-slate-500">{m.role}</span>
              )}
            </li>
          ))}
          {members.length === 0 && !membersQ.isLoading && (
            <li className="py-2 text-sm text-slate-500">No members yet.</li>
          )}
        </ul>

        {/* Org assignment */}
        {isOwner && (
          <div className="mt-5 border-t border-slate-100 pt-4">
            <label className="text-sm">
              <span className="text-slate-600">Organization</span>
              <select
                value={currentOrgId ?? ""}
                onChange={(e) => assignOrg.mutate(e.target.value || null)}
                className="mt-1 block w-full rounded border border-slate-300 px-2 py-2 text-sm"
              >
                <option value="">No organization (private)</option>
                {(orgsQ.data ?? []).map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </label>
            <p className="mt-1 text-xs text-slate-500">
              Members of the selected organization can access this board.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
